import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { ProductScheme, EventScheme } from "client-v2";

import {
	Calendar,
	Facebook,
	Link,
	MapPin,
	MessageCircle,
	Share,
	Ticket,
	Twitter,
} from "lucide-react";
import {
	Text,
	Color,
	Button,
	Label,
	ColorV2,
	Menu,
} from "@adoptaunabuelo/react-components";
import ResumeStyled from "../../../components/container/ResumeContainer";

const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const Chip = styled.div`
	display: flex;
	padding: 6px 14px;
	border-radius: 20px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	width: fit-content;
	align-items: center;
	justify-content: center;
	gap: 6px;
`;
const Icon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	width: 44px;
	border-radius: 8px;
	background-color: ${ColorV2.surface.background};
`;

const Resume = (props: Props) => {
	const pastDue = props.product.startDate
		? dayjs() > dayjs(props.product.startDate.iso)
		: false;

	const renderCell = (data: {
		icon: React.ComponentType<any>;
		iconColor?: string;
		iconBackground?: string;
		title: string;
		subtitle?: string;
		style?: {
			margin?: number;
			fontSize?: number;
			textColor?: string;
			fontWeight?: number;
		};
	}) => (
		<Row
			style={{
				gap: 12,
				marginBottom: data.style?.margin ? data.style.margin : 0,
				justifyContent: data.subtitle ? "flex-start" : "center",
			}}
		>
			<Icon
				style={{
					backgroundColor: data.iconBackground
						? data.iconBackground
						: ColorV2.surface.background,
				}}
			>
				<data.icon
					style={{
						minHeight: 24,
						minWidth: 24,
					}}
					height={24}
					width={24}
					color={
						data.iconColor
							? data.iconColor
							: ColorV2.text.neutralHard
					}
				/>
			</Icon>
			<Col
				style={{
					flex: 1,
				}}
			>
				<Text
					type="p"
					weight="medium"
					style={{
						fontSize: data.style?.fontSize,
						fontWeight: data.style?.fontWeight,
						color: data.style?.textColor,
					}}
				>
					{data.title}
				</Text>
				{data.subtitle && (
					<Text
						type="p"
						style={{
							color: ColorV2.text.neutralMedium,
						}}
					>
						{data.subtitle}
					</Text>
				)}
			</Col>
		</Row>
	);

	return (
		<ResumeStyled>
			<Row
				style={{
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 16,
				}}
			>
				<Chip>
					<Ticket
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
					<Text type="p2">Evento</Text>
				</Chip>
				<Menu
					id={"share"}
					position={"bottom-left"}
					menuStyle={{ width: 200, top: 32, right: 0 }}
					options={[
						{
							id: "twitter",
							label: "Twitter",
							icon: <Twitter height={20} width={20} />,
						},
						{
							id: "facebook",
							label: "Facebook",
							icon: <Facebook height={20} width={20} />,
						},
						{
							id: "whatsapp",
							label: "Whatsapp",
							icon: <MessageCircle height={20} width={20} />,
						},
						{
							id: "url",
							label: "Copiar enlace",
							icon: <Link height={20} width={20} />,
						},
					]}
					Icon={
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<Share width={20} height={20} />
							<Text type="b2">Compartir</Text>
						</div>
					}
					onClick={props.onShareClick}
				/>
			</Row>
			<Text type="h3" weight="semibold" style={{ marginBottom: 24 }}>
				{props.product.title}
			</Text>
			<Row
				style={{
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				{props.product.price !== 0 ? (
					<Row style={{ alignItems: "center" }}>
						<Text type="h3" weight="semibold">
							{props.product.price}€
						</Text>
						<Text type="p" style={{ marginLeft: 8 }}>
							por persona
						</Text>
					</Row>
				) : props.product.data && props.product.data.corporate ? (
					<Text type="h5" weight="medium">
						Información del evento
					</Text>
				) : (
					<Text
						type="h3"
						weight="semibold"
						style={{ color: Color.text.primary }}
					>
						Gratis
					</Text>
				)}
				{props.showLabel && (
					<Label
						text="Últimas plazas"
						color={Color.status.color.error}
						style={{
							backgroundColor: Color.status.color.errorDefault,
							borderRadius: 30,
							padding: "0px 12px",
						}}
					/>
				)}
				{pastDue && (
					<Label
						text="Evento finalizado"
						color={Color.status.color.error}
						style={{
							backgroundColor: Color.status.color.errorDefault,
							borderRadius: 30,
							padding: "0px 12px",
						}}
					/>
				)}
			</Row>
			<Row style={{ marginTop: 24 }}>
				{renderCell({
					icon: Calendar,
					title: dayjs(props.product.startDate?.iso).locale("es").format(
						"dddd, D [de] MMMM [de] YYYY"
					),
					subtitle: `De ${dayjs(props.product.startDate?.iso).format(
						"HH:mm"
					)} a ${dayjs(props.product.endDate?.iso).format("HH:mm")}`,
				})}
			</Row>
			<Row style={{ marginTop: 16 }}>
				{renderCell({
					icon: MapPin,
					title: props.event.shortAddress,
					subtitle: props.event.address,
				})}
			</Row>
			{props.onClick && (
				<Button
					style={{ marginTop: 32 }}
					disabled={pastDue}
					onClick={props.onClick}
				>
					{props.product.data && props.product.data.corporate
						? "Confirmar asistencia"
						: "Reservar plaza"}
				</Button>
			)}
		</ResumeStyled>
	);
};
export default Resume;
export interface Props {
	product: ProductScheme;
	event: EventScheme;
	showLabel?: boolean;
	onClick?: () => void;
	onShareClick: (op: { id: string }) => void;
}
